import React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@repo/tokens";
import type { QuestionItemProps } from "./types";
import "./QuestionItem.css";
import { Box, HStack, Text, Icon, Pills, Image } from "@repo/ui";

function isOpenStageBackendStatus(
  status: string | undefined,
  doubtCardStatus: string | undefined,
): boolean {
  const s = (status ?? "").trim().toUpperCase();
  const c = (doubtCardStatus ?? "").trim().toLowerCase();
  return s === "OPEN" || c === "open";
}

function getStatusPillColor(
  status: string,
): "success" | "error" | "warning" | "info" | "neutral" {
  switch (status.toLowerCase()) {
    case "solved":
      return "success";
    case "not resolved":
    case "dismissed":
      return "error";
    case "in progress":
    case "feedback pending":
      return "warning";
    case "pending":
      return "info";
    case "open":
      return "info";
    default:
      return "neutral";
  }
}

function resolveDisplayStatus(
  status: string | undefined,
  doubtCardStatus: string | undefined,
): string {
  const s = (status ?? "").trim().toUpperCase();
  const cardStatus = (doubtCardStatus ?? "").trim();
  if (cardStatus.toLowerCase() === "feedback pending")
    return "Feedback Pending";
  /** Open-stage doubts: surface as “In Progress” per product / Figma. */
  if (s === "OPEN" || cardStatus.toLowerCase() === "open") {
    return "In Progress";
  }
  if (s === "RESOLVED") return "Solved";
  if (s === "NOT_RESOLVED" || s === "NOTRESOLVED") return "Not Resolved";
  if (s === "DISMISSED") return "Dismissed";
  return cardStatus || status || "";
}

export type QuestionItemComponentProps = {
  question: QuestionItemProps;
  /** Row matches open Get answers chat (doubts web). */
  isActiveChat?: boolean;
  onBookmarkPress?: () => void;
  onQuestionPress?: () => void;
};

export function QuestionItem({
  question,
  isActiveChat = false,
  onBookmarkPress,
  onQuestionPress,
}: QuestionItemComponentProps) {
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();

  const displayStatus = resolveDisplayStatus(
    question.status,
    question.doubtCardStatus,
  );
  const statusKey = displayStatus.toLowerCase();
  const statusVariantClass = statusKey.replace(/\s+/g, "-");
  const statusLabel = t(`status.${statusKey}`, {
    defaultValue: displayStatus,
  });
  const pillColor = getStatusPillColor(displayStatus);

  const useOpenStageInProgressPill = isOpenStageBackendStatus(
    question.status,
    question.doubtCardStatus,
  );
  const useSolvedStatusPill = statusKey === "solved";

  const cardClassName =
    "question-item-card" +
    (onQuestionPress ? " question-item-card--clickable" : "") +
    (isActiveChat ? " question-item-card--active-chat" : "");

  return (
    <Box
      as="article"
      className={cardClassName}
      onClick={onQuestionPress}
      role={onQuestionPress ? "button" : undefined}
      tabIndex={onQuestionPress ? 0 : undefined}
      onKeyDown={
        onQuestionPress
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onQuestionPress();
              }
            }
          : undefined
      }
    >
      <HStack
        className="question-item-top"
        justify="space-between"
        align="flex-start"
        gap="xs"
      >
        <HStack
          className="question-item-tags"
          gap="xs"
          style={{ flexWrap: "wrap" }}
        >
          {useOpenStageInProgressPill ? (
            <Box
              as="span"
              className="question-item-status-pill question-item-status-pill-open-in-progress question-item-status-pill--in-progress"
            >
              <Text variant="label" size="small" weight="emphasized" as="span">
                {statusLabel}
              </Text>
            </Box>
          ) : useSolvedStatusPill ? (
            <Box
              as="span"
              className="question-item-status-pill question-item-status-pill-solved question-item-status-pill--solved"
            >
              <Text variant="label" size="small" weight="emphasized" as="span">
                {statusLabel}
              </Text>
            </Box>
          ) : (
            <Box
              as="span"
              className={`question-item-status-pill question-item-status-pill--${statusVariantClass}`}
            >
              <Pills
                label={statusLabel}
                size="xs"
                variant="subtle"
                color={pillColor}
                py={2}
                px={8}
              />
            </Box>
          )}
          <Box as="span" className="question-item-subject-pill">
            <Pills
              label={question.subject}
              size="xs"
              variant="outline"
              py={2}
              px={8}
            />
          </Box>
        </HStack>
        {onBookmarkPress ? (
          <button
            type="button"
            className={
              question.isBookmarked
                ? "question-item-bookmark is-bookmarked"
                : "question-item-bookmark"
            }
            onClick={(e) => {
              e.stopPropagation();
              onBookmarkPress();
            }}
            aria-label={t("toggle_bookmark_a11y")}
          >
            <Icon
              name={
                question.isBookmarked
                  ? "selected_bookmark"
                  : "bookmarkIconDoubts"
              }
              width={25}
              height={25}
              color={theme.colors.primary[40]}
            />
          </button>
        ) : (
          <span
            className={
              question.isBookmarked
                ? "question-item-bookmark is-bookmarked"
                : "question-item-bookmark"
            }
            aria-hidden
          >
            <Icon
              name={
                question.isBookmarked
                  ? "selected_bookmark"
                  : "bookmarkIconDoubts"
              }
              width={25}
              height={25}
              color={theme.colors.primary[40]}
            />
          </span>
        )}
      </HStack>

      {question.isImageDoubt && question.image ? (
        <Image
          source={question.image}
          style={{
            width: "100%",
            maxHeight: 150,
            objectFit: "cover",
            borderRadius: 8,
          }}
          decorative={true}
          accessibilityLabel={t("question_image_a11y")}
        />
      ) : question.text ? (
        <p
          className="question-item-text"
          /**
           * The text area is scrollable for long questions. Keep clicks inside
           * it from reopening the question chat when the user only wanted to
           * scroll; the rest of the card still handles the press.
           */
          onClick={(e) => e.stopPropagation()}
        >
          {question.text}
        </p>
      ) : question.image ? (
        <Image
          source={question.image}
          style={{
            width: "100%",
            maxHeight: 150,
            objectFit: "cover",
            borderRadius: 8,
          }}
          decorative={true}
          accessibilityLabel={t("question_image_a11y")}
        />
      ) : (
        <p className="question-item-text">{t("no_text_available")}</p>
      )}

      {question.date && (
        <HStack className="question-item-footer" align="center" gap="xs">
          <Icon
            name="Calender"
            width={15}
            height={15}
            color="var(--colors-text-2, #666666)"
          />
          <Text as="span" className="question-item-footer-date">
            {question.date}
          </Text>
        </HStack>
      )}
    </Box>
  );
}

export default QuestionItem;
